import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Award, ArrowLeft, CheckCircle, Eye, EyeOff, Anchor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const InscriptionMoniteur = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: '',
    telephone: '',
    annee_diplome: ''
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) newErrors.prenom = 'Le prénom est requis';
    if (!formData.email.trim()) newErrors.email = "L'email est requis";
    if (!formData.password) newErrors.password = 'Le mot de passe est requis';
    if (formData.password.length < 6) newErrors.password = 'Le mot de passe doit faire au moins 6 caractères';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    if (!formData.telephone.trim()) newErrors.telephone = 'Le téléphone est requis';
    if (!formData.annee_diplome) newErrors.annee_diplome = "L'année de diplôme est requise";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post('/auth/register-ancien-moniteur', {
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        password: formData.password,
        telephone: formData.telephone,
        annee_diplome: parseInt(formData.annee_diplome)
      });
      setSuccess(true);
      toast.success('Inscription enregistrée !');
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="inscription-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-display font-bold text-[#0F172A] mb-2">
            Inscription enregistrée !
          </h1>
          <p className="text-slate-600 mb-6">
            Votre demande d'inscription en tant que <strong>Moniteur</strong> a été transmise au centre de formation.
            Vous recevrez un email de confirmation une fois votre inscription validée.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-blue-700 font-medium mb-1">Prochaines étapes :</p>
            <ul className="text-sm text-blue-600 space-y-1">
              <li>Le centre validera votre inscription sous 24-48h</li>
              <li>Vous serez notifié par email une fois validé</li>
              <li>Vous pourrez alors accéder à votre espace moniteur</li>
            </ul>
          </div>
          <Link to="/login">
            <Button className="w-full bg-[#8DC63F] hover:bg-[#7db336]" data-testid="btn-back-login">
              Retour à la connexion
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-lg space-y-6">
          <div className="text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#8DC63F] mb-4">
              <ArrowLeft className="h-4 w-4" />
              Retour à la connexion
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <Award className="h-8 w-8 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-1">
              Inscription Moniteur
            </h1>
            <p className="text-slate-500 text-sm">
              Vous êtes diplômé BPJEPS ? Inscrivez-vous pour accéder à votre espace moniteur.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="moniteur-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  placeholder="Votre prénom"
                  className={errors.prenom ? 'border-red-400' : ''}
                  data-testid="input-prenom"
                />
                {errors.prenom && <p className="text-xs text-red-500">{errors.prenom}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Votre nom"
                  className={errors.nom ? 'border-red-400' : ''}
                  data-testid="input-nom"
                />
                {errors.nom && <p className="text-xs text-red-500">{errors.nom}</p>}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="votre@email.com"
                className={errors.email ? 'border-red-400' : ''}
                data-testid="input-email"
              />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="telephone">Téléphone *</Label>
                <Input
                  id="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                  className={errors.telephone ? 'border-red-400' : ''}
                  data-testid="input-telephone"
                />
                {errors.telephone && <p className="text-xs text-red-500">{errors.telephone}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="annee_diplome">Année Diplôme *</Label>
                <Input
                  id="annee_diplome"
                  type="number"
                  min="2000"
                  max={currentYear}
                  value={formData.annee_diplome}
                  onChange={(e) => setFormData({ ...formData, annee_diplome: e.target.value })}
                  placeholder={currentYear.toString()}
                  className={errors.annee_diplome ? 'border-red-400' : ''}
                  data-testid="input-annee-diplome"
                />
                {errors.annee_diplome && <p className="text-xs text-red-500">{errors.annee_diplome}</p>}
              </div>
            </div>

            <div className="space-y-1 relative">
              <Label htmlFor="password">Mot de passe *</Label>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={errors.password ? 'border-red-400' : ''}
                data-testid="input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-[30px] p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={errors.confirmPassword ? 'border-red-400' : ''}
                data-testid="input-confirm-password"
              />
              {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
            </div>

            <Button
              type="submit"
              className="w-full bg-[#8DC63F] hover:bg-[#7db336] text-white font-semibold h-11"
              disabled={loading}
              data-testid="btn-submit-inscription"
            >
              {loading ? 'Inscription en cours...' : "S'inscrire"}
            </Button>
          </form>
        </div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex bg-gradient-to-br from-indigo-600 to-blue-800 items-center justify-center p-12">
        <div className="text-center text-white max-w-md">
          <Anchor className="h-20 w-20 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl font-display font-bold mb-4">Espace Moniteur</h2>
          <p className="text-lg opacity-80 mb-8">
            Retrouvez les offres d'emploi et restez connecté avec la communauté FISL.
          </p>
          <div className="space-y-3 text-left bg-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span>Consultez les offres d'emploi</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span>Gérez votre profil professionnel</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
              <span>Postulez directement aux annonces</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InscriptionMoniteur;
